
import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import WithPaymentWrapper from "../../Common/WithPaymentWrapper";
import PurchaseMyAddInsDetails from "./PurchaseMyAddInsDetails";
import SsoServices from "../../../../services/SsoServices";
import {
  SSO_MY_ADDINS_SUBSCRIPTION_DETAILS,
  SSO_SUBSCRIBER_ADD_CONFIGURATION,
  SSO_SUBSCRIBER_MANAGE_CONFIGURATION,
  SSO_SUBSCRIBER_edit_CONFIGURATION,
  SSO_SUBSCRIBER_DELETE_CONFIGURATION,
  SSO_SUBSCRIBER_SUBSCRIPTION_MANAGEMENT,
  SSO_SUBSCRIBER_STOP_SUBSCRIPTION,
  ADD_TO_CART,
} from "../../../../constants/api-constants";
import { transactionType } from "../../../../constants/common";
import {
  DATE_FORMAT,
  FREQUENCY_LABELS,
  toArray,
  getSubscriptionTypeDisplay,
  shouldShowRenewButton,
  shouldShowStopButton,
  getEffectiveFromDate,
  getEffectiveToDate,
  determineTrialLabel,
  mergeManageConfiguration,
  getManagedFieldDefinitions,
  buildConfigurationPayload,
  mergeAddInData,
  getMappedDetailsAddIn,
  addSubscriberRenewalId,
} from "./purchaseMyAddInsUtils";

export default function PurchaseMyAddInsDetailsPage() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading add-in details...");
  const [addIn, setAddIn] = useState(null);
  const [subscriptionManagement, setSubscriptionManagement] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const selectedAddInFromState =
    location?.state?.selectedCartAddIn ||
    location?.state?.addInRow ||
    location?.state?.addIn ||
    null;

  const numericId = Number(id);

  // Derive computed states for subscription management
  const showRenewButton = useMemo(
    () => (subscriptionManagement ? shouldShowRenewButton(subscriptionManagement) : false),
    [subscriptionManagement],
  );

  const showStopButton = useMemo(
    () => (subscriptionManagement ? shouldShowStopButton(subscriptionManagement) : false),
    [subscriptionManagement],
  );

  const subscriptionTypeDisplay = useMemo(
    () => (subscriptionManagement ? getSubscriptionTypeDisplay(subscriptionManagement) : "N/A"),
    [subscriptionManagement],
  );

  const frequencyDisplay = useMemo(
    () =>
      subscriptionManagement ? FREQUENCY_LABELS[subscriptionManagement.frequency] || subscriptionManagement.subscriptionFrequency || "N/A" : "N/A",
    [subscriptionManagement],
  );

  const effectiveFromDate = useMemo(
    () => (subscriptionManagement ? getEffectiveFromDate(subscriptionManagement) : null),
    [subscriptionManagement],
  );

  const effectiveToDate = useMemo(
    () => (subscriptionManagement ? getEffectiveToDate(subscriptionManagement) : null),
    [subscriptionManagement],
  );

  const trialLabel = useMemo(
    () => (subscriptionManagement ? determineTrialLabel(subscriptionManagement) : ""),
    [subscriptionManagement],
  );


  /**
   * Handles stop subscription action
   */
  const handleStopSubscription = async () => {
    try {
      setSyncing(true);

      const response = await SsoServices.ssoPost(
        SSO_SUBSCRIBER_STOP_SUBSCRIPTION(numericId),
        {},
      );

      if (response?.responseStatus?.isSuccess) {
        toast.success(
          response?.responseStatus?.description ||
            `Subscription will remain active until ${effectiveToDate?.format(DATE_FORMAT)}.`,
        );

        history.push("/purchase");
        return true;
      } else {
        toast.error(
          response?.responseStatus?.description || "Failed to stop subscription",
        );
        return false;
      }
    } catch (error) {
      console.error("Error stopping subscription:", error);
      toast.error(error?.message || "Error stopping subscription");
      return false;
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Handles renew subscription - adds to cart and redirects to checkout
   */
  const handleRenewSubscription = async () => {
    if (!numericId) {
      toast.error("Invalid add-in details");
      return false;
    }

    const frequency = subscriptionManagement?.frequency || 0;

    const payload = {
      itemId: numericId,
      quantity: 1,
      transactionType: transactionType.ADDIN_PURCHASE,
      frequency: frequency,
      currency: "USD",
    };

    try {
      setSyncing(true);
      const response = await SsoServices.ssoPost(ADD_TO_CART, payload);

      if (response?.responseStatus?.isSuccess) {
        addSubscriberRenewalId(numericId);
        toast.success("Added to cart. Redirecting to checkout...");
        history.push("/purchase/cart");
        return true;
      } else {
        toast.error(
          response?.responseStatus?.description || "Failed to add to cart",
        );
        return false;
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
      toast.error("Failed to add item to cart");
      return false;
    } finally {
      setSyncing(false);
    }
  };

  const refreshManageConfiguration = async (addInId, fallbackState) => {
    const response = await SsoServices.ssoGet(
      SSO_SUBSCRIBER_MANAGE_CONFIGURATION(addInId),
    );

    setAddIn((prev) =>
      mergeManageConfiguration(prev || fallbackState, response?.result || null),
    );

    return response;
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadingMessage("Loading add-in details...");

    const fallbackAddIn =
      selectedAddInFromState &&
      Number(selectedAddInFromState.addInId) === numericId
        ? selectedAddInFromState
        : null;

    if (fallbackAddIn) {
      setAddIn(mergeAddInData(null, fallbackAddIn));
    }

    const fetchAddInDetails = async () => {
      const [detailResult, configurationResult] = await Promise.allSettled([
        SsoServices.ssoGet(SSO_MY_ADDINS_SUBSCRIPTION_DETAILS(numericId)),
        SsoServices.ssoGet(SSO_SUBSCRIBER_MANAGE_CONFIGURATION(numericId)),
      ]);

      if (!mounted) return;

      let nextAddIn = mergeAddInData(
        detailResult.status === "fulfilled" ? detailResult.value?.result || null : null,
        fallbackAddIn,
      );

      if (configurationResult.status === "fulfilled") {
        nextAddIn = mergeManageConfiguration(
          nextAddIn,
          configurationResult.value?.result || null,
        );
      }

      const hasEnterpriseName =
        typeof nextAddIn?.enterpriseName === "string"
          ? nextAddIn.enterpriseName.trim().length > 0
          : nextAddIn?.enterpriseName !== null &&
            nextAddIn?.enterpriseName !== undefined;

      if (!hasEnterpriseName) {
        const subscriptionMgmtResult = await Promise.allSettled([
          SsoServices.ssoGet(SSO_SUBSCRIBER_SUBSCRIPTION_MANAGEMENT(numericId)),
        ]);
        if (!mounted) return;

        if (subscriptionMgmtResult[0].status === "fulfilled") {
          setSubscriptionManagement(subscriptionMgmtResult[0].value?.result || null);
        } else {
          setSubscriptionManagement(null);
        }
      } else {
        setSubscriptionManagement(null);
      }

      setAddIn(nextAddIn);
      setLoading(false);
    };

    fetchAddInDetails().catch(() => {
      if (!mounted) return;
      setAddIn(mergeAddInData(null, fallbackAddIn));
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [id, selectedAddInFromState, numericId]);

  const mappedAddIn = useMemo(() => {
    if (!addIn) return null;
    return getMappedDetailsAddIn(addIn);
  }, [addIn]);

  const handleCreateConfiguration = async (formData) => {
    if (!numericId || !mappedAddIn) return false;

    const fieldDefinitions = getManagedFieldDefinitions(mappedAddIn);
    const payload = buildConfigurationPayload(
      numericId,
      formData,
      fieldDefinitions,
    );

    try {
      await SsoServices.ssoPost(
        SSO_SUBSCRIBER_ADD_CONFIGURATION(numericId),
        payload,
      );
      await refreshManageConfiguration(numericId, addIn);
      toast.success("Configuration added.");
      return true;
    } catch (error) {
      toast.error(error?.message || "Failed to save configuration.");
      return false;
    }
  };

  const handleEditConfiguration = async (formData) => {
    if (!numericId || !mappedAddIn || !formData?.id) return false;

    const fieldDefinitions = getManagedFieldDefinitions(mappedAddIn);
    const existingConfiguration = toArray(mappedAddIn?.configurations).find(
      (configuration) => String(configuration.id) === String(formData.id),
    );
    const payload = buildConfigurationPayload(
      numericId,
      formData,
      fieldDefinitions,
      formData.id,
      existingConfiguration?.values,
    );

    try {
      await SsoServices.ssoPut(
        SSO_SUBSCRIBER_edit_CONFIGURATION(numericId, formData.id),
        payload,
      );
      await refreshManageConfiguration(numericId, addIn);
      toast.success("Configuration updated.");
      return true;
    } catch (error) {
      toast.error(error?.message || "Failed to save configuration.");
      return false;
    }
  };

  const handleDeleteConfiguration = async (row) => {
    if (!numericId || !row?.id) return false;

    try {
      await SsoServices.ssoDelete(
        SSO_SUBSCRIBER_DELETE_CONFIGURATION(numericId, row.id),
      );
      await refreshManageConfiguration(numericId, addIn);
      toast.success("Configuration deleted.");
      return true;
    } catch (error) {
      toast.error(error?.message || "Failed to delete configuration.");
      return false;
    }
  };

  if (loading) {
    return (
      <WithPaymentWrapper>
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body1">{loadingMessage}</Typography>
        </Box>
      </WithPaymentWrapper>
    );
  }

  if (!mappedAddIn) return <div>Add-in not found</div>;

  return (
    <WithPaymentWrapper>
      <PurchaseMyAddInsDetails
        addIn={mappedAddIn}
        subscriptionManagement={subscriptionManagement}
        showRenewButton={showRenewButton}
        showStopButton={showStopButton}
        subscriptionTypeDisplay={subscriptionTypeDisplay}
        frequencyDisplay={frequencyDisplay}
        effectiveFromDate={effectiveFromDate}
        effectiveToDate={effectiveToDate}
        trialLabel={trialLabel}
        syncing={syncing}
        dateFormat={DATE_FORMAT}
        accessGrantedBy={mappedAddIn?.accessGrantedBy}
        onBack={() => history.push("/purchase")}
        onRenew={handleRenewSubscription}
        onUnsubscribe={handleStopSubscription}
        onCreateConfiguration={handleCreateConfiguration}
        onEditConfiguration={handleEditConfiguration}
        onDeleteConfiguration={handleDeleteConfiguration}
      />
    </WithPaymentWrapper>
  );
}
